import React, { ReactNode } from 'react';
import {
  TrackedLink as Link,
  TrackedLink,
} from 'components/common/TrackedLink';
import { StyledLinkedResultSnippet } from 'components/common/search/LinkedResultSnippets';
import { StyledResultTitle } from 'pages/search/SearchResult';
import { useAssetAccess } from 'components/asset-access';
import styled from 'styled-components';
import { SearchContext } from 'components/layouts/research-layout/SearchContext';
import pickBy from 'lodash/fp/pickBy';
import { useLocation } from 'react-router';
import qs from 'util/qs';
import { PublicationSectionSearchEdge } from '__generated__/graphql';
import { SecondarySourceLink } from 'features/link-helpers/SecondarySourceLink';
import { useSearchParams } from 'react-router-dom';

function LinkToTitleSearch({
  titleOnlawId,
  children,
}: {
  titleOnlawId?: string;
  children?: ReactNode;
}) {
  const { optionFilters, queryString } = React.useContext(SearchContext);
  const location = useLocation();

  const options = pickBy((value, key) => value && !key.startsWith('title_'))(
    optionFilters,
  );

  return (
    <TrackedLink
      to={{
        pathname: location.pathname,
        search: `?${qs.stringify({
          ...queryString,
          optionFilters: Object.keys(options)
            .concat(`title_${titleOnlawId}`)
            .join(','),
        })}`,
      }}
    >
      {children}
    </TrackedLink>
  );
}

export interface PublicationResultProps {
  result: NonNullable<PublicationSectionSearchEdge>;
  className?: string;
}

export function PublicationResult({
  result,
  className,
}: PublicationResultProps) {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('query');
  const { snippets } = result;

  const secondarySourcesTitleParts = result!.node!.secondarySourcesTitleParts!;
  const secondarySourcesTitlePart = secondarySourcesTitleParts[0];

  const resourceType = secondarySourcesTitlePart
    ? secondarySourcesTitlePart.title!.resourceType!.name
    : '';

  const productTitle = secondarySourcesTitlePart
    ? secondarySourcesTitlePart.title!.productTitle
    : '';

  const practiceArea =
    secondarySourcesTitlePart?.title?.practiceAreas[0]?.practiceArea;
  const productCode = secondarySourcesTitlePart?.titleProductCode;
  const onlawId = secondarySourcesTitlePart?.onlawId;

  const { hasAccess, hasSecondarySourcesTrial } = useAssetAccess();

  const hasAccessToProduct =
    hasAccess!(secondarySourcesTitlePart!.titleProductCode ?? '') ||
    hasSecondarySourcesTrial;

  return (
    <StyledPublicationResult className={className || ''}>
      <header className={!hasAccessToProduct ? 'locked' : ''}>
        <StyledResultTitle>
          {hasAccessToProduct ? (
            <SecondarySourceLink
              practiceAreaSlug={practiceArea?.slug || undefined}
              productCode={productCode}
              onlawId={onlawId}
              query={query}
            >
              {secondarySourcesTitlePart ? secondarySourcesTitlePart.name : ''}
            </SecondarySourceLink>
          ) : (
            <>
              <i className="fas fa-lock" />
              {secondarySourcesTitlePart ? secondarySourcesTitlePart.name : ''}
            </>
          )}
        </StyledResultTitle>

        {resourceType && productTitle ? (
          <section>
            <div className="resource-type">{resourceType}</div>
            {hasAccessToProduct ? null : (
              <div className="access-link">
                <span>Out of Plan</span>
                <Link to="/account/purchases">How do I get access?</Link>
              </div>
            )}
            <div className="product-title">
              <LinkToTitleSearch
                titleOnlawId={secondarySourcesTitlePart?.titleOnlawId}
              >
                {productTitle}
              </LinkToTitleSearch>
            </div>
          </section>
        ) : null}
      </header>

      <StyledSecondarySourceSnippets>
        {(snippets || []).map(snippet =>
          hasAccessToProduct ? (
            <SecondarySourceLink
              key={snippet?.position}
              practiceAreaSlug={practiceArea?.slug || undefined}
              productCode={productCode}
              onlawId={onlawId}
              query={query || undefined}
              hash={snippet?.position || ''}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: snippet?.text || '',
                }}
              />
            </SecondarySourceLink>
          ) : (
            <span
              key={snippet?.position}
              dangerouslySetInnerHTML={{
                __html: snippet?.text || '',
              }}
            />
          ),
        )}
      </StyledSecondarySourceSnippets>
    </StyledPublicationResult>
  );
}

const StyledSecondarySourceSnippets = styled(StyledLinkedResultSnippet)`
  a,
  span {
    margin-bottom: 15px;
    line-height: 1.25em;
  }
`;

const StyledPublicationResult = styled.li`
  margin: 0 0 5px;
  padding: 7px 0;

  header {
    &.locked {
      border-bottom: transparent;
      margin-bottom: 0px;
    }
    h3 {
      a,
      a:visited {
        color: var(--COLOR_NICE_BLUE);
      }
      margin-bottom: 13px;
      position: relative;
      .fa-lock {
        position: absolute;
        left: -20px;
        top: 4px;
        font-size: 15px;
        color: var(--COLOR_ORANGEY_YELLOW);
      }
    }
    section {
      display: flex;
      gap: 12px;
      align-items: center;
      margin-bottom: 12px;
      div {
        flex: 0 1 auto;
        font-family: 'Zilla Slab', serif;
        font-weight: bold;
        &.resource-type {
          color: var(--COLOR_COOL_GRAY);
          font-size: 12px;
          flex: 0 1 auto;
          text-transform: uppercase;
        }
        &.access-link {
          display: flex;
          font-family: var(--FONT_SERIF);
          font-size: 14px;
          font-weight: bold;
          line-height: 1;
          span {
            color: var(--COLOR_ORANGEY_YELLOW);
            margin-right: 4px;
            padding-right: 4px;
            border-right: 1px solid
              color-mix(in srgb, var(--COLOR_BLACK) 50%, transparent);
          }
        }
        &.product-title {
          font-family: var(--FONT_SANS_SERIF);
          font-size: 13px;
          font-weight: 900;
          a,
          a:visited {
            color: var(--COLOR_COOL_BLUE);
          }
          &:before {
            content: 'in ';
            font-weight: normal;
            font-style: italic;
          }
        }
      }
    }
  }
  span a {
    display: block;
  }
`;
